import React, { createContext, useContext, useEffect, useRef } from 'react';
import Lenis from 'lenis';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useLocation } from 'react-router-dom';
import { prefersReducedMotion } from './tokens';

gsap.registerPlugin(ScrollTrigger);

const LenisContext = createContext(null);
export const useLenis = () => useContext(LenisContext);

// A hash is an in-page anchor only when it names an element id; `#token=…`
// links (set-password) and anything else that isn't a selector are ignored.
function anchorFor(hash) {
  if (!hash || !/^#[A-Za-z][\w-]*$/.test(hash)) return null;
  return document.getElementById(hash.slice(1));
}

// Smooth scroll is OFF by default (native scrolling feels faster on modest
// machines); set ENABLE_SMOOTH to true to bring Lenis back. ScrollTrigger
// works on the window either way; route changes still reset scroll.
const ENABLE_SMOOTH = false;
export default function LenisProvider({ children }) {
  const lenisRef = useRef(null);
  const location = useLocation();

  useEffect(() => {
    if (!ENABLE_SMOOTH || prefersReducedMotion()) return undefined;
    const lenis = new Lenis({ lerp: 0.09, smoothWheel: true, wheelMultiplier: 0.9 });
    lenisRef.current = lenis;
    window.__lenis = lenis; // exposed for scripted QA (scrollTo with immediate)
    lenis.on('scroll', ScrollTrigger.update);
    const tick = (time) => lenis.raf(time * 1000);
    gsap.ticker.add(tick);
    gsap.ticker.lagSmoothing(0);
    return () => {
      gsap.ticker.remove(tick);
      lenis.destroy();
      lenisRef.current = null;
      delete window.__lenis;
    };
  }, []);

  // Route change: jump to top (or a hash target) and refresh triggers.
  useEffect(() => {
    const hash = anchorFor(location.hash);
    if (lenisRef.current) {
      lenisRef.current.scrollTo(hash || 0, { immediate: true });
    } else if (hash) {
      hash.scrollIntoView();
    } else {
      window.scrollTo(0, 0);
    }
    const id = window.setTimeout(() => ScrollTrigger.refresh(), 120);
    return () => window.clearTimeout(id);
  }, [location.pathname, location.hash]);

  return <LenisContext.Provider value={lenisRef}>{children}</LenisContext.Provider>;
}
